<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { signedOutNavigation } from '@/lib/signedOutNavigation'

const props = withDefaults(defineProps<{
  brandName: string
  logoUrl: string
  returnUrl: string | null
  continuation: string | null
  completeSignoutUrl: string
  navigate?: (url: string) => void
}>(), {
  navigate: (url: string) => { window.location.assign(url) },
})

const destination = signedOutNavigation(props.returnUrl, props.continuation, props.completeSignoutUrl)
const secondsRemaining = ref(15)
let timer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  timer = setInterval(() => {
    secondsRemaining.value -= 1
    if (secondsRemaining.value === 0) {
      clearInterval(timer)
      props.navigate(destination)
    }
  }, 1000)
})

onUnmounted(() => clearInterval(timer))

function goNow(event: MouseEvent) {
  const link = event.currentTarget as HTMLAnchorElement
  if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    && !event.defaultPrevented && (!link.target || link.target === '_self')) {
    clearInterval(timer)
  }
}
</script>

<template>
  <main
    class="shell"
    data-testid="signed-out"
  >
    <section class="card">
      <header>
        <img
          class="card__logo"
          :src="logoUrl"
          :alt="brandName"
        >
        <h1 class="card__title">
          You are signed out
        </h1>
        <p>
          Returning you to your app, where you can sign in again.
        </p>
      </header>
      <p
        aria-live="polite"
        aria-atomic="true"
        data-testid="countdown"
      >
        Returning in {{ secondsRemaining }} {{ secondsRemaining === 1 ? 'second' : 'seconds' }}.
      </p>
      <a
        class="signed-out__link"
        :href="destination"
        @click="goNow"
      >Go now</a>
    </section>
  </main>
</template>

<style scoped>
.signed-out__link {
  display: inline-block;
  padding: 0.625rem 1rem;
  border-radius: var(--radius);
  background: var(--primary);
  color: var(--primary-foreground);
  font-weight: 600;
  text-decoration: none;
}

.signed-out__link:focus-visible {
  outline: 3px solid var(--ring);
  outline-offset: 3px;
}
</style>
